import { Settings2 } from "lucide-react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Checkbox } from "@/components/ui/checkbox"

export interface ColumnOption {
  id: string
  label: string
  isCustomField?: boolean
}

interface ColumnSelectorProps {
  columns: ColumnOption[]
  visibleCount: number
  totalCount: number
  isColumnVisible: (id: string) => boolean
  onToggleColumn: (id: string) => void
  onShowAll: () => void
  onHideAll: () => void
}

export function ColumnSelector({
  columns,
  visibleCount,
  totalCount,
  isColumnVisible,
  onToggleColumn,
  onShowAll,
  onHideAll,
}: ColumnSelectorProps) {
  const allVisible = visibleCount === totalCount

  // Separate standard columns from custom fields
  const standardColumns = columns.filter((c) => !c.isCustomField)
  const customFieldColumns = columns.filter((c) => c.isCustomField)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="inline-flex h-9 items-center gap-1.5 rounded-md border border-input px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted">
          <Settings2 size={14} />
          <span>
            {visibleCount}/{totalCount}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-64 p-0"
      >
        {/* Header with toggle all */}
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-xs font-medium text-muted-foreground">
            Kolumny
          </span>
          <button
            onClick={allVisible ? onHideAll : onShowAll}
            className="text-xs font-medium text-primary transition-colors hover:opacity-80"
          >
            {allVisible ? "Ukryj wszystkie" : "Pokaz wszystkie"}
          </button>
        </div>

        {/* Column list */}
        <div className="max-h-72 overflow-y-auto py-1">
          {standardColumns.map((col) => (
            <ColumnCheckboxItem
              key={col.id}
              label={col.label}
              checked={isColumnVisible(col.id)}
              onToggle={() => onToggleColumn(col.id)}
            />
          ))}

          {customFieldColumns.length > 0 && (
            <>
              <div className="border-t border-border mx-3 my-1" />
              <div className="px-3 py-1">
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  Pola niestandardowe
                </span>
              </div>
              {customFieldColumns.map((col) => (
                <ColumnCheckboxItem
                  key={col.id}
                  label={col.label}
                  checked={isColumnVisible(col.id)}
                  onToggle={() => onToggleColumn(col.id)}
                />
              ))}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function ColumnCheckboxItem({
  label,
  checked,
  onToggle,
}: {
  label: string
  checked: boolean
  onToggle: () => void
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted/50">
      <Checkbox
        checked={checked}
        onCheckedChange={onToggle}
      />
      <span className="truncate">{label}</span>
    </label>
  )
}
